/**
 * Display-only cleanup helpers for legacy staff-name storage.
 *
 * Historical staff rows have parenthetical location/role suffixes baked
 * into the stored name (e.g. "Cruz (Ajax)", "St. Martin (Ajax/Oshaw,
 * Teacher)"). The database value is authoritative and MUST NOT be
 * rewritten — these helpers strip the parenthesised tail for display
 * only. Hoisted from Staff/List once Today needed the same rule (per
 * the "second module triggers hoist" convention).
 */

/** Capitalise first letter, lowercase the rest — used across staff labels. */
export const cap = (s: string | null | undefined): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

/** Strip a trailing "( ... )" from a single name segment (display only). */
export const stripStaffNameSuffix = (s: string | null | undefined): string =>
  s ? s.replace(/\s*\([^)]*\)\s*$/g, "").trim() : "";

/**
 * Clean-name convenience for a full "First Last" (or similar) string.
 * Splits nothing — assumes the caller passes the whole rendered name
 * from an API that already concatenated it.
 */
export const cleanStaffDisplayName = (name: string | null | undefined): string =>
  stripStaffNameSuffix(name || "").trim();

/** Build a display name from split fields, stripping suffix on each part. */
export const buildStaffDisplayName = (row: {
  teamFirstName?: string | null;
  middle_name?: string | null;
  teamLastName?: string | null;
}): string =>
  [cap(stripStaffNameSuffix(row.teamFirstName)),
   cap(stripStaffNameSuffix(row.middle_name)),
   cap(stripStaffNameSuffix(row.teamLastName))]
    .filter(Boolean).join(" ");
